<template>
  <div
    class="kpb-features"
    v-if="data.content"
    :data-kpb-variant="variant"
    :style="`--kpb-features-columns: ${columns}; --kpb-features-padding: ${padding}; --kpb-features-icon-background-color: ${iconBackgroundColor};`"
  >
    <Card
      v-for="(item, index) in data.content"
      :key="'card' + index"
      theme="flat"
      tag="div"
      :thumbnail="item.icon ? item.icon.url : undefined"
      :class="[
        'kpb-features__icon',
        `kpb-features__border-theme--${borderTheme}`,
        `kpb-features__placement--${placementComp}`,
      ]"
    >
      <div
        v-if="item.content"
        v-html="item.content"
        class="kpb-features__content"
        :class="{ 'kpb-features__content-bold': boldContent }"
      ></div>
      <div v-if="item.subtitle" class="kpb-features__content">{{ item.subtitle }}</div>
    </Card>
  </div>
</template>

<script>
import Card from '@kvass/card'

export default {
  props: {
    data: {
      type: Object,
      default: () => ({
        content: [],
      }),
    },
    columns: {
      type: Number,
      default: 4,
    },
    variant: {
      type: String,
      default: 'none',
      validator: val => ['circle', 'underline', 'none'].includes(val),
      enums: ['circle', 'underline', 'none'],
    },
    padding: {
      type: String,
      default: '1.1rem',
    },
    iconBackgroundColor: {
      type: String,
      default: 'inherit',
    },
    placement: {
      type: String,
      default: 'left',
      validator: val => ['left', 'center', 'right'].includes(val),
      enums: ['left', 'center', 'right'],
    },

    borderTheme: {
      type: String,
      default: 'black',
      validator(val) {
        return ['primary', 'secondary', 'white', 'black', 'custom'].includes(val)
      },
      enums: ['primary', 'secondary', 'white', 'black', 'custom'],
    },

    boldContent: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    placementComp() {
      if (!this.placement && this.variant === 'circle') return 'center'
      return this.placement
    },
  },

  components: {
    Card,
  },
}
</script>

<style lang="scss">
.kpb-features {
  $padding-inline: var(--kpb-features-padding-inline, 1rem);
  $gap: var(--kpb-features-gap, 4rem);
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  width: 100%;
  gap: $gap;

  &__placement {
    &--left {
      .kvass-card__header {
        margin-inline: $padding-inline;
        background-position: left;
        transform: translate(calc(var(--kpb-features-padding) * -1));
      }
      .kpb-features__content {
        padding-inline: $padding-inline;
        text-align: left;
      }
    }
    &--center {
      .kvass-card__header {
        margin-left: auto;
        margin-right: auto;
        background-position: center;
      }
      .kpb-features__content {
        text-align: center;
      }
    }
    &--right {
      .kvass-card__header {
        margin-inline: $padding-inline;
        margin-left: auto;
        background-position: right;
        transform: translate(calc(var(--kpb-features-padding) * 1));
      }
      .kpb-features__content {
        padding-inline: $padding-inline;
        text-align: right;
      }
    }
  }

  &__border-theme {
    &--primary {
      &,
      .kvass-card__header {
        border-color: var(--primary);
      }
    }

    &,
    &--secondary {
      .kvass-card__header {
        border-color: var(--secondary);
      }
    }

    &,
    &--white {
      .kvass-card__header {
        border-color: #ffffff;
      }
    }

    &,
    &--black {
      .kvass-card__header {
        border-color: #000000;
      }
    }

    &,
    &--custom {
      .kvass-card__header {
        border-color: var(--custom-background-color, var(--primary));
      }
    }
  }

  .kvass-card {
    background-color: inherit;
    flex-grow: 0;
    --kvass-card-spacing: 0px 0px;
    --kvass-card-border-radius: 0px;
  }

  &__icon {
    width: calc((100% / var(--kpb-features-columns)) - #{$gap} + #{$gap} / var(--kpb-features-columns));

    @include respond-below('phone') {
      width: calc((100% / 2) - #{$gap} + #{$gap} / 2);
    }

    .kvass-card__header {
      $size: var(--kpb-features-size, 7rem);
      background-size: contain;
      background-repeat: no-repeat;
      background-origin: content-box;
      background-color: inherit;
      min-height: 0px;
      margin-bottom: 10px;
      width: $size;
      max-height: calc(#{$size} + 1rem);
      min-height: $size;
      border-radius: 100%;
      padding: var(--kpb-features-padding);
    }
  }

  &__content {
    word-break: break-word;
    line-height: 1.7;

    strong {
      font-size: 1.25rem;
    }
    margin-bottom: 0.5rem;
    font-size: 1rem;
    p {
      margin: 0;
    }

    &-bold {
      font-size: 1.25rem;
      font-weight: 700;
    }
  }
  /* Variants */
  &[data-kpb-variant='circle'] {
    .kpb-features {
      &__icon {
        .kvass-card__header {
          background-color: var(--kpb-features-icon-background-color);
          border-width: 2px;
          border-style: solid;
        }
      }
    }
  }

  &[data-kpb-variant='underline'] {
    .kpb-features {
      &__icon {
        border-bottom-style: solid;
        border-bottom-width: 7px;

        @include respond-below('phone') {
          width: 85%;
        }

        .kvass-card__header {
          border-bottom: 0;
        }
      }
    }
  }

  &[data-kpb-variant='none'] {
    .kvass-card__header {
      border-radius: none;
      border: none !important;
    }
    .kpb-features {
      &__icon {
        border: none;
      }
    }
  }
}
</style>
